import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { X, Send, Search, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { DESIGN } from '../DesignSystem';

// ================================================
// BASE MODAL COMPONENT
// ================================================
export const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div 
        className="relative rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[85vh] overflow-hidden animate-scale-in"
        style={{ backgroundColor: DESIGN.colors.card }}
        data-testid="command-center-modal"
      >
        <div 
          className="flex items-center justify-between p-5 border-b"
          style={{ borderColor: DESIGN.colors.border }}
        >
          <h3 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>{title}</h3>
          <button 
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" style={{ color: DESIGN.colors.textMuted }} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 70px)' }}>
          {children}
        </div>
      </div>

      <style>{`
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-scale-in {
          animation: scaleIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

// ================================================
// SCHEDULE MEETING MODAL
// ================================================
export const ScheduleMeetingModal = ({ isOpen, onClose, team, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(30);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTeam = useMemo(() => {
    if (!searchQuery.trim()) return team;
    const query = searchQuery.toLowerCase();
    return team.filter(member => 
      member.name.toLowerCase().includes(query) ||
      (member.title && member.title.toLowerCase().includes(query))
    );
  }, [team, searchQuery]);

  const selectedMembers = useMemo(() => {
    return team.filter(m => selectedParticipants.includes(m.id));
  }, [team, selectedParticipants]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !date || !time || selectedParticipants.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit({
        title,
        description,
        participant_ids: selectedParticipants,
        scheduled_time: new Date(`${date}T${time}`).toISOString(),
        duration_minutes: duration,
        meeting_type: 'general'
      });
      toast.success('Meeting scheduled successfully!');
      onClose();
      setTitle('');
      setDescription('');
      setSelectedParticipants([]);
      setDate('');
      setTime('');
      setSearchQuery('');
    } catch (err) {
      toast.error('Failed to schedule meeting');
    } finally {
      setLoading(false);
    }
  };

  const toggleParticipant = (id) => {
    setSelectedParticipants(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const removeParticipant = (id) => {
    setSelectedParticipants(prev => prev.filter(p => p !== id));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Schedule Meeting">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            Meeting Title *
          </label>
          <Input 
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Weekly Team Sync"
            className="rounded-xl"
            data-testid="meeting-title-input"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            Description
          </label>
          <Textarea 
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Agenda and notes..."
            rows={2}
            className="rounded-xl"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
              Date *
            </label>
            <Input 
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-xl"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
              Time *
            </label>
            <Input 
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="rounded-xl"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            Duration
          </label>
          <select 
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="w-full px-4 py-2.5 rounded-xl border transition-colors"
            style={{ borderColor: DESIGN.colors.border, backgroundColor: DESIGN.colors.card }}
          >
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={45}>45 minutes</option>
            <option value={60}>1 hour</option>
            <option value={90}>1.5 hours</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            Participants * ({selectedParticipants.length} selected)
          </label>
          
          {selectedMembers.length > 0 && (
            <div 
              className="flex flex-wrap gap-2 mb-3 p-3 rounded-xl"
              style={{ backgroundColor: DESIGN.colors.primarySubtle }}
            >
              {selectedMembers.map(member => (
                <span 
                  key={member.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium"
                  style={{ backgroundColor: DESIGN.colors.card, color: DESIGN.colors.primary }}
                >
                  <span 
                    className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ backgroundColor: DESIGN.colors.primary }}
                  >
                    {member.avatar}
                  </span>
                  {member.name}
                  <button 
                    type="button"
                    onClick={() => removeParticipant(member.id)}
                    className="hover:opacity-70"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
            <Input 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search team members..."
              className="pl-10 rounded-xl"
              data-testid="participant-search"
            />
          </div>
          
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {filteredTeam.length === 0 ? (
              <p className="text-sm p-3" style={{ color: DESIGN.colors.textMuted }}>No members found</p>
            ) : (
              filteredTeam.map(member => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => toggleParticipant(member.id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition-all"
                  style={{ 
                    backgroundColor: selectedParticipants.includes(member.id) 
                      ? DESIGN.colors.primarySubtle 
                      : DESIGN.colors.background,
                    color: selectedParticipants.includes(member.id) 
                      ? DESIGN.colors.primary 
                      : DESIGN.colors.textSecondary,
                    border: `2px solid ${selectedParticipants.includes(member.id) ? DESIGN.colors.primary : 'transparent'}`
                  }}
                >
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ backgroundColor: selectedParticipants.includes(member.id) ? DESIGN.colors.primary : DESIGN.colors.textMuted }}
                  >
                    {member.avatar}
                  </div>
                  <span className="font-medium">{member.name}</span>
                </button>
              ))
            )}
          </div>
        </div>
        
        <div className="flex justify-end gap-3 pt-3">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading}
            className="rounded-xl"
            style={{ 
              background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
              boxShadow: DESIGN.shadows.glowPrimary
            }}
            data-testid="schedule-meeting-submit"
          >
            {loading ? 'Scheduling...' : 'Schedule Meeting'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ================================================
// DELEGATE TASK MODAL
// ================================================
export const DelegateTaskModal = ({ isOpen, onClose, team, projects, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTeam = useMemo(() => {
    if (!searchQuery.trim()) return team;
    const query = searchQuery.toLowerCase();
    return team.filter(member => 
      member.name.toLowerCase().includes(query) ||
      (member.title && member.title.toLowerCase().includes(query))
    );
  }, [team, searchQuery]);

  const selectedAssignee = useMemo(() => {
    return team.find(m => m.id === assigneeId);
  }, [team, assigneeId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !assigneeId) {
      toast.error('Please provide a title and assignee');
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit({
        title,
        description,
        assignee_id: assigneeId,
        project_id: projectId || null,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
        priority
      });
      toast.success('Task delegated successfully!');
      onClose();
      setTitle('');
      setDescription('');
      setAssigneeId('');
      setProjectId('');
      setDueDate('');
      setPriority('medium');
      setSearchQuery('');
    } catch (err) {
      toast.error('Failed to delegate task');
    } finally {
      setLoading(false);
    }
  };

  const priorityConfigs = {
    low: { bg: 'rgba(59, 130, 246, 0.1)', text: '#3B82F6', ring: '#3B82F6' },
    medium: { bg: DESIGN.colors.amberSubtle, text: '#D97706', ring: '#F59E0B' },
    high: { bg: 'rgba(249, 115, 22, 0.1)', text: '#EA580C', ring: '#F97316' },
    critical: { bg: DESIGN.colors.errorSubtle, text: DESIGN.colors.error, ring: DESIGN.colors.error }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delegate Task">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            Task Title *
          </label>
          <Input 
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Review Q3 metrics report"
            className="rounded-xl"
            data-testid="delegate-task-title"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            Description
          </label>
          <Textarea 
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Task details..."
            rows={2}
            className="rounded-xl"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            Assign To *
          </label>
          
          {selectedAssignee && (
            <div 
              className="flex items-center gap-3 mb-3 p-3 rounded-xl"
              style={{ backgroundColor: DESIGN.colors.primarySubtle }}
            >
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white"
                style={{ 
                  background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                  boxShadow: DESIGN.shadows.glowPrimary
                }}
              >
                {selectedAssignee.avatar}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: DESIGN.colors.primary }}>{selectedAssignee.name}</p>
                <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>{selectedAssignee.active_tasks} active tasks</p>
              </div>
              <button 
                type="button"
                onClick={() => setAssigneeId('')}
                className="p-1.5 rounded-lg hover:bg-white/50"
              >
                <X className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
              </button>
            </div>
          )}
          
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
            <Input 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search team members..."
              className="pl-10 rounded-xl"
              data-testid="assignee-search"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
            {filteredTeam.length === 0 ? (
              <p className="text-sm p-3 col-span-2" style={{ color: DESIGN.colors.textMuted }}>No members found</p>
            ) : (
              filteredTeam.map(member => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => setAssigneeId(member.id)}
                  className="flex items-center gap-3 p-3 rounded-xl text-left transition-all"
                  style={{ 
                    backgroundColor: assigneeId === member.id ? DESIGN.colors.primarySubtle : DESIGN.colors.background,
                    border: `2px solid ${assigneeId === member.id ? DESIGN.colors.primary : 'transparent'}`
                  }}
                >
                  <div 
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold text-white"
                    style={{ backgroundColor: assigneeId === member.id ? DESIGN.colors.primary : DESIGN.colors.textMuted }}
                  >
                    {member.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: DESIGN.colors.textPrimary }}>{member.name}</p>
                    <p className="text-xs truncate" style={{ color: DESIGN.colors.textMuted }}>{member.active_tasks} tasks</p>
                  </div>
                  {member.available && (
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500" title="Available"></span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
              Project
            </label>
            <select 
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border text-sm"
              style={{ borderColor: DESIGN.colors.border, backgroundColor: DESIGN.colors.card }}
            >
              <option value="">No project</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
              Due Date
            </label>
            <Input 
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="rounded-xl"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            Priority
          </label>
          <div className="flex gap-2">
            {Object.entries(priorityConfigs).map(([key, config]) => (
              <button
                key={key}
                type="button"
                onClick={() => setPriority(key)}
                className="px-4 py-2 rounded-xl text-sm font-semibold capitalize transition-all"
                style={{ 
                  backgroundColor: priority === key ? config.bg : DESIGN.colors.background,
                  color: priority === key ? config.text : DESIGN.colors.textSecondary,
                  border: `2px solid ${priority === key ? config.ring : 'transparent'}`
                }}
              >
                {key}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex justify-end gap-3 pt-3">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading}
            className="rounded-xl gap-2"
            style={{ 
              background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
              boxShadow: DESIGN.shadows.glowPrimary
            }}
            data-testid="delegate-task-submit"
          >
            <UserPlus className="w-4 h-4" />
            {loading ? 'Delegating...' : 'Delegate Task'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ================================================
// SEND MESSAGE MODAL
// ================================================
export const SendMessageModal = ({ isOpen, onClose, team, onSubmit }) => {
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTeam = useMemo(() => {
    if (!searchQuery.trim()) return team;
    const query = searchQuery.toLowerCase();
    return team.filter(member => 
      member.name.toLowerCase().includes(query) ||
      (member.title && member.title.toLowerCase().includes(query))
    );
  }, [team, searchQuery]);

  const selectedRecipients = useMemo(() => {
    return team.filter(m => recipients.includes(m.id));
  }, [team, recipients]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content || recipients.length === 0) {
      toast.error('Please add recipients and a message');
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit({
        recipient_ids: recipients,
        subject,
        content,
        is_urgent: isUrgent
      });
      toast.success('Message sent!');
      onClose();
      setRecipients([]);
      setSubject('');
      setContent('');
      setIsUrgent(false);
      setSearchQuery('');
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const toggleRecipient = (id) => {
    setRecipients(prev => 
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const removeRecipient = (id) => {
    setRecipients(prev => prev.filter(r => r !== id));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send Quick Message">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            Recipients * ({recipients.length} selected)
          </label>
          
          {selectedRecipients.length > 0 && (
            <div 
              className="flex flex-wrap gap-2 mb-3 p-3 rounded-xl"
              style={{ backgroundColor: DESIGN.colors.blueSubtle }}
            >
              {selectedRecipients.map(member => (
                <span 
                  key={member.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium"
                  style={{ backgroundColor: DESIGN.colors.card, color: DESIGN.colors.blue }}
                >
                  <span 
                    className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ backgroundColor: DESIGN.colors.blue }}
                  >
                    {member.avatar}
                  </span>
                  {member.name}
                  <button 
                    type="button"
                    onClick={() => removeRecipient(member.id)}
                    className="hover:opacity-70"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
            <Input 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search recipients..."
              className="pl-10 rounded-xl"
              data-testid="recipient-search"
            />
          </div>
          
          <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
            {filteredTeam.length === 0 ? (
              <p className="text-sm p-3" style={{ color: DESIGN.colors.textMuted }}>No members found</p>
            ) : (
              filteredTeam.map(member => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => toggleRecipient(member.id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition-all"
                  style={{ 
                    backgroundColor: recipients.includes(member.id) ? DESIGN.colors.blueSubtle : DESIGN.colors.background,
                    color: recipients.includes(member.id) ? DESIGN.colors.blue : DESIGN.colors.textSecondary,
                    border: `2px solid ${recipients.includes(member.id) ? DESIGN.colors.blue : 'transparent'}`
                  }}
                >
                  <div 
                    className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ backgroundColor: recipients.includes(member.id) ? DESIGN.colors.blue : DESIGN.colors.textMuted }}
                  >
                    {member.avatar}
                  </div>
                  <span className="font-medium">{member.name}</span>
                </button>
              ))
            )}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            Subject
          </label>
          <Input 
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Quick update..."
            className="rounded-xl"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
            Message *
          </label>
          <Textarea 
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Type your message..."
            rows={4}
            className="rounded-xl"
            data-testid="message-content"
          />
        </div>
        
        <label 
          className="flex items-center gap-3 cursor-pointer p-3 rounded-xl transition-colors hover:bg-red-50"
          style={{ backgroundColor: isUrgent ? DESIGN.colors.errorSubtle : 'transparent' }}
        >
          <input 
            type="checkbox"
            checked={isUrgent}
            onChange={(e) => setIsUrgent(e.target.checked)}
            className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
          />
          <span className="text-sm font-medium" style={{ color: isUrgent ? DESIGN.colors.error : DESIGN.colors.textPrimary }}>
            Mark as urgent
          </span>
        </label>
        
        <div className="flex justify-end gap-3 pt-3">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading}
            className="rounded-xl gap-2"
            style={{ 
              background: `linear-gradient(135deg, ${DESIGN.colors.blue} 0%, #2563EB 100%)`,
              boxShadow: DESIGN.shadows.glowPrimary
            }}
            data-testid="send-message-submit"
          >
            <Send className="w-4 h-4" />
            {loading ? 'Sending...' : 'Send Message'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default { Modal, ScheduleMeetingModal, DelegateTaskModal, SendMessageModal };
